/* eslint-disable react/jsx-max-depth */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */

import { Route, Switch } from 'react-router-dom';

import { Visit } from '@dreryk/egabinet-visit-frontend-api';

import { appendTestableElementAttributes, ControlsBar, Redirect, StyledFlex } from '@dreryk/react-components';
import { isDemo } from '@dreryk/demo-variant-frontend';

import { protectFeature } from '~libs.shared.system';
import { visitPaths } from '~libs.desktop.shared.paths';
import { ReceptionServicesDetails } from '~libs.shared.reception-service';
import {
   VideoConsultationBackAction,
   VideoConsultationButton,
   VideoConsultationUrlClipboard,
} from '~libs.desktop.video-consultation';
import { createPatientWithExternalIdentifierProtector } from '~libs.shared.visit';
import { PatientEntitlementToMedicalServiceStatus } from '~libs.desktop.patient-entitlement-to-medical-service';
import { PatientGroups } from '~libs.desktop.shared.patient-groups';

import { MedicalProfileHeader } from '../appointment/medicalProfile/MedicalProfileHeader';
import { ResourceByPatientGenderGuard } from '../common/ResourceByPatientGenderGuard';
import { VisitHeader } from '../common/visit/header/VisitHeader';
import { VisitHeaderActions } from '../common/visit/header/VisitHeaderActionsPortal';
import { VisitHeaderPatientDetails } from '../common/visit/header/VisitHeaderPatientDetails';
import { VisitHeaderPatientNameLink } from '../common/visit/header/VisitHeaderPatientNameLink';
import { VisitDateTime } from '../common/visit/VisitDateTime';
import { VisitStopwatch } from '../common/visit/VisitStopwatch';
import { VisitTimer } from '../common/visit/VisitTimer';
import { ConsentTemplatePanel } from '../features/consentTemplate/ConsentTemplatePanel';
import { DocumentTemplatePanel } from '../features/documentTemplate/DocumentTemplatePanel';
import { DischargeSummaryPanel } from '../features/electronicDocuments/dischargeSummary/DischargeSummaryPanel/DischargeSummaryPanel';
import { MedicalLeavePanel } from '../features/ezla';
import { MedicalCertificatePanel } from '../features/medicalCertificate/MedicalCertificatePanel';
import { MedicalDevicesPanel } from '../features/medicalDevices/MedicalDevicesPanel';
import { PrescriptionIssuingPanel } from '../features/prescriptions';
import {
   ReferralToAnalyticalExaminationPanel,
   ReferralToDiagnosticExaminationPanel,
   ReferralToHospitalPanel,
   ReferralToPsychiatricHospitalPanel,
   ReferralToRehabilitationPanel,
   ReferralToSpecialistPanel,
} from '../features/referral';
import { EducationalMaterialsPanel } from '../features/educationalMaterials/EducationalMaterialsPanel';
import { visitTranslationKeys } from '../translations';
import { VisitSidePanel } from '../visitSidePanel/VisitSidePanel';
import { VisitFormContextProvider } from '../common/visitForm/VisitFormContext';

import { getServiceLocationIdFromVisitLocation } from './visitLocation/getServiceLocationIdFromVisitLocation';
import { ConsentTemplateAction } from './actionBar/consentTemplate/ConsentTemplateAction';
import { DocumentTemplateAction } from './actionBar/documentTemplate/DocumentTemplateAction';
import { ElectronicDocumentsAction } from './actionBar/ElectronicDocumentsAction';
import { GoBackToVisitAction } from './actionBar/GoBackToVisitAction';
import { GoToMedicalDevicesAction } from './actionBar/GoToMedicalDevicesAction';
import { GoToMedicalLeaveAction } from './actionBar/GoToMedicalLeaveAction';
import { GoToNextVisitAction } from './actionBar/GoToNextVisitAction';
import { GoToPrescriptionAction } from './actionBar/GoToPrescriptionAction';
import { VisitActionBar } from './actionBar/VisitActionBar';
import { VisitForm } from './form/VisitForm';
import { VisitFormLoader } from './form/VisitForm.loader';
import { PatientPregnancyStatus } from './patientPregnancy/PatientPregnancyStatus';
import { VisitLocation } from './visitLocation/VisitLocation';
import { StyledVisitPanel } from './VisitPanel.styled';
import {
   clockVisitor,
   encounterModeVisitor,
   findDefaultVisitDisease,
   getVideoConsultationVisitLocation,
} from './VisitPanel.utils';
import { VisitRouteChangingGuard } from './VisitRouteChangingGuard';
import { VisitConfidentialityLevelDropdown } from './visitConfidentialityLevel/VisitConfidentialityLevelDropdown';
import { VisitEncounterModeDropdown } from './visitEncounterMode/VisitEncounterModeDropdown';
import { GoToEducationalMaterialsAction } from './actionBar/GoToEducationalMaterialsAction';

type FullVisitPanelProps = {
   readonly visit: Visit;
};

// eslint-disable-next-line max-lines-per-function
export const FullVisitPanel = (props: FullVisitPanelProps): JSX.Element => {
   const protectByPatientWithExternalIdentifier = createPatientWithExternalIdentifierProtector(props.visit.patient);

   return (
      <VisitFormContextProvider>
         <VisitActionBar>
            <VisitRouteChangingGuard />
            <StyledVisitPanel {...appendTestableElementAttributes('visitPanel')}>
               <VisitHeader
                  title={
                     <StyledFlex gap={4} alignItems={'center'}>
                        <VisitHeaderPatientNameLink
                           patient={props.visit.patient}
                           backActionLabel={visitTranslationKeys.goBackToVisit}
                        />
                        <PatientGroups patientGroups={props.visit.patient.patientGroups} />
                        {
                           // TODO DREGAB-29797
                           protectFeature(
                              'experimental',
                              <Route path={visitPaths.VISIT_INTERVIEW_PATH}>
                                 <MedicalProfileHeader
                                    patientId={props.visit.patient.id}
                                    readonly={props.visit.reopened}
                                 />
                              </Route>,
                              null
                           )
                        }
                     </StyledFlex>
                  }
                  details={
                     <>
                        <VisitHeaderPatientDetails patient={props.visit.patient} />
                        {protectFeature(
                           'privateMedicalServicesReadOnly',
                           null,
                           <ReceptionServicesDetails appointmentId={props.visit.appointmentId} />
                        )}
                     </>
                  }
                  actions={
                     <VisitHeaderActions.Container>
                        {clockVisitor(props.visit)({
                           timer: ({ beginDate, plannedBeginDate, plannedEndDate }) => (
                              <VisitTimer
                                 beginDate={beginDate}
                                 plannedBeginDate={plannedBeginDate}
                                 plannedEndDate={plannedEndDate}
                              />
                           ),

                           stopwatch: ({ beginDate }) => <VisitStopwatch beginDate={beginDate} />,
                        })}
                     </VisitHeaderActions.Container>
                  }
               />
               <ControlsBar testId={'visit'}>
                  <ControlsBar.Main position={'left'}>
                     <VisitDateTime
                        plannedBeginDate={props.visit.plannedBeginDate}
                        plannedEndDate={props.visit.plannedEndDate}
                     />
                     <VisitLocation
                        visitId={props.visit.id}
                        disabled={!props.visit.cancellable}
                        {...props.visit.visitLocation}
                     />
                     {protectFeature(
                        'performingVideoConsultations',
                        <VisitEncounterModeDropdown
                           patientId={props.visit.patient.id}
                           visitId={props.visit.id}
                           visitEncounterMode={props.visit.encounterMode}
                        />
                     )}
                     {isDemo() ? null : (
                        <VisitConfidentialityLevelDropdown
                           visitId={props.visit.id}
                           confidentialityLevel={props.visit.confidentialityLevel}
                        />
                     )}
                  </ControlsBar.Main>

                  <ControlsBar.Supplementary position={'right'}>
                     {encounterModeVisitor(props.visit)({
                        visitVideoConsultation: () => (
                           <>
                              <VideoConsultationBackAction
                                 appointmentId={props.visit.appointmentId}
                                 label={visitTranslationKeys.goBackToVisit}
                                 toOriginLabel={visitTranslationKeys.goBackToVisitFromVideoCall}
                                 location={getVideoConsultationVisitLocation(props.visit.id)}
                              />
                              <VideoConsultationUrlClipboard
                                 appointmentId={props.visit.appointmentId}
                                 label={visitTranslationKeys.copyVideoConsultationLinkForPatient}
                              />
                              <VideoConsultationButton
                                 appointmentId={props.visit.appointmentId}
                                 label={visitTranslationKeys.startVideoConsultation}
                              />
                           </>
                        ),
                        visitOnSite: () => null,
                     })}
                  </ControlsBar.Supplementary>

                  <Switch>
                     <Route path={visitPaths.VISIT_PATH} exact>
                        <Redirect path={visitPaths.getVisitInterviewPath({ params: { visitId: props.visit.id } })} />
                     </Route>
                     <Route path={visitPaths.VISIT_INTERVIEW_PATH}>
                        <VisitFormLoader patientId={props.visit.patient.id}>
                           {({ chronicDiseases }) => (
                              <VisitForm visit={props.visit} chronicDiseases={chronicDiseases} />
                           )}
                        </VisitFormLoader>
                        <VisitHeaderActions.Portal>
                           <PatientEntitlementToMedicalServiceStatus
                              visitId={props.visit.id}
                              checkDate={props.visit.beginDate}
                              readonly={props.visit.reopened}
                              patientReferenceIdentifier={props.visit.patient.referenceIdentifier}
                           />
                           <ResourceByPatientGenderGuard patientId={props.visit.patient.id} gender={'WOMAN'}>
                              <PatientPregnancyStatus
                                 pregnancyStatus={props.visit.visitDetails.pregnancyStatus}
                                 visitId={props.visit.id}
                              />
                           </ResourceByPatientGenderGuard>
                        </VisitHeaderActions.Portal>
                     </Route>

                     {protectByPatientWithExternalIdentifier(
                        'issuingPrescriptions',
                        <Route path={visitPaths.PRESCRIPTIONS_PATH}>
                           <PrescriptionIssuingPanel
                              patientId={props.visit.patient.id}
                              patientAddress={props.visit.patient.address}
                              patientReferenceIdentifier={props.visit.patient.referenceIdentifier!}
                              locationId={getServiceLocationIdFromVisitLocation(props.visit.visitLocation)}
                              visitId={props.visit.id}
                              visitDiagnoses={props.visit.diagnoses}
                              appointmentId={props.visit.appointmentId}
                              inPersonExaminationPerformed={Boolean(
                                 props.visit.visitDetails.inPersonExaminationPerformed
                              )}
                              documentationScope={props.visit.documentationScope}
                           />
                           <VisitHeaderActions.Portal>
                              <PatientEntitlementToMedicalServiceStatus
                                 visitId={props.visit.id}
                                 checkDate={props.visit.beginDate}
                                 readonly={props.visit.reopened}
                                 patientReferenceIdentifier={props.visit.patient.referenceIdentifier}
                              />
                              <ResourceByPatientGenderGuard patientId={props.visit.patient.id} gender={'WOMAN'}>
                                 <PatientPregnancyStatus
                                    pregnancyStatus={props.visit.visitDetails.pregnancyStatus}
                                    visitId={props.visit.id}
                                 />
                              </ResourceByPatientGenderGuard>
                           </VisitHeaderActions.Portal>
                        </Route>
                     )}

                     {protectByPatientWithExternalIdentifier(
                        'issuingMedicalLeaves',
                        <Route path={visitPaths.MEDICAL_LEAVE_PATH}>
                           <MedicalLeavePanel visit={props.visit} />
                        </Route>
                     )}

                     {protectByPatientWithExternalIdentifier(
                        'issuingReferrals',
                        <Route path={visitPaths.ELECTRONIC_REFERRAL_TO_HOSPITAL_PATH} exact>
                           <ReferralToHospitalPanel visit={props.visit} />
                        </Route>
                     )}

                     {protectByPatientWithExternalIdentifier(
                        'issuingReferrals',
                        <Route path={visitPaths.REFERRAL_TO_HOSPITAL_PATH} exact>
                           <ReferralToHospitalPanel visit={props.visit} />
                        </Route>
                     )}

                     {protectByPatientWithExternalIdentifier(
                        'issuingReferrals',
                        <Route path={visitPaths.ELECTRONIC_REFERRAL_TO_DIAGNOSTIC_EXAMINATION_PATH} exact>
                           <ReferralToDiagnosticExaminationPanel visit={props.visit} />
                        </Route>
                     )}

                     {protectByPatientWithExternalIdentifier(
                        'issuingReferrals',
                        <Route path={visitPaths.REFERRAL_TO_DIAGNOSTIC_EXAMINATION_PATH} exact>
                           <ReferralToDiagnosticExaminationPanel visit={props.visit} />
                        </Route>
                     )}

                     <Route path={visitPaths.MEDICAL_CERTIFICATE_PATH} exact>
                        <MedicalCertificatePanel
                           visitId={props.visit.id}
                           dictionaryValidOn={props.visit.beginDate}
                           diagnosisSuggestions={props.visit.diagnoses}
                        />
                     </Route>

                     {protectByPatientWithExternalIdentifier(
                        'issuingReferrals',
                        <Route path={visitPaths.REFERRAL_TO_ANALYTICAL_EXAMINATION_PATH} exact>
                           <ReferralToAnalyticalExaminationPanel visitId={props.visit.id} />
                        </Route>
                     )}

                     {protectByPatientWithExternalIdentifier(
                        'issuingReferrals',
                        <Route path={visitPaths.ELECTRONIC_REFERRAL_TO_SPECIALIST_PATH} exact>
                           <ReferralToSpecialistPanel visit={props.visit} />
                        </Route>
                     )}

                     {protectByPatientWithExternalIdentifier(
                        ['issuingDischargeSummary'],
                        <Route path={visitPaths.DISCHARGE_SUMMARY_PATH} exact>
                           <DischargeSummaryPanel
                              visitId={props.visit.id}
                              patientId={props.visit.patient.id}
                              dictionaryValidOn={props.visit.beginDate}
                              visitDiagnoses={props.visit.diagnoses}
                              visitLocation={props.visit.visitLocation}
                              reopened={props.visit.reopened}
                           />
                        </Route>
                     )}

                     {protectByPatientWithExternalIdentifier(
                        'issuingReferrals',
                        <Route path={visitPaths.REFERRAL_TO_SPECIALIST_PATH} exact>
                           <ReferralToSpecialistPanel visit={props.visit} />
                        </Route>
                     )}

                     {protectByPatientWithExternalIdentifier(
                        'issuingReferrals',
                        <Route path={visitPaths.REFERRAL_TO_REHABILITATION_PATH} exact>
                           <ReferralToRehabilitationPanel visit={props.visit} />
                        </Route>
                     )}

                     {protectByPatientWithExternalIdentifier(
                        'issuingReferrals',
                        <Route path={visitPaths.REFERRAL_TO_PSYCHIATRIC_HOSPITAL_PATH} exact>
                           <ReferralToPsychiatricHospitalPanel visit={props.visit} />
                        </Route>
                     )}

                     {protectByPatientWithExternalIdentifier(
                        'issuingPaperDocumentsFromTemplate',
                        <Route path={visitPaths.DOCUMENT_TEMPLATE_PATH} exact>
                           <DocumentTemplatePanel patientId={props.visit.patient.id} />
                        </Route>
                     )}

                     <Route path={visitPaths.CONSENT_TEMPLATE_PATH} exact>
                        <ConsentTemplatePanel patientId={props.visit.patient.id} />
                     </Route>

                     <Route path={visitPaths.VISIT_EDUCATIONAL_MATERIALS_PATH} exact>
                        <EducationalMaterialsPanel
                           visitId={props.visit.id}
                           patientEmail={props.visit.patient.emailAddress}
                        />
                     </Route>

                     {protectByPatientWithExternalIdentifier(
                        'issuingMedicalDeviceOrders',
                        <Route path={visitPaths.VISIT_MEDICAL_DEVICES_PATH}>
                           <MedicalDevicesPanel
                              patientId={props.visit.patient.id}
                              patientAge={props.visit.patient.age!}
                              visitId={props.visit.id}
                              visitDisease={findDefaultVisitDisease(props.visit) ?? undefined}
                           />
                        </Route>
                     )}

                     <Route>
                        <Redirect path={visitPaths.getVisitInterviewPath({ params: { visitId: props.visit.id } })} />
                     </Route>
                  </Switch>
               </ControlsBar>
            </StyledVisitPanel>
            <VisitSidePanel
               patientId={props.visit.patient.id}
               currentVisitId={props.visit.id}
               visitDiagnoses={props.visit.diagnoses}
            />
            <GoToEducationalMaterialsAction visitId={props.visit.id} />
            <GoBackToVisitAction visitId={props.visit.id} />
            <GoToNextVisitAction patientId={props.visit.patient.id} />
            {protectFeature(
               'issuingPaperDocumentsFromTemplate',
               <DocumentTemplateAction visitId={props.visit.id} />,
               null
            )}
            <ConsentTemplateAction patientId={props.visit.patient.id} visitId={props.visit.id} />
            {protectByPatientWithExternalIdentifier(
               'issuingMedicalDeviceOrders',
               <GoToMedicalDevicesAction visitId={props.visit.id} patient={props.visit.patient} />,
               null
            )}
            {protectByPatientWithExternalIdentifier(
               'issuingReferrals',
               <ElectronicDocumentsAction visitId={props.visit.id} reopened={props.visit.reopened} />,
               null
            )}
            {protectByPatientWithExternalIdentifier(
               'issuingPrescriptions',
               <GoToPrescriptionAction visitId={props.visit.id} />,
               null
            )}
            {protectByPatientWithExternalIdentifier(
               'issuingMedicalLeaves',
               <GoToMedicalLeaveAction visitId={props.visit.id} />,
               null
            )}
         </VisitActionBar>
      </VisitFormContextProvider>
   );
};
